/**
* Per-locale copy for /{locale}/longstay pillar mirrors.
* Locales: en · zh · ja · ko · vi · id.
*/
export type LongstayLocale = "en" | "zh" | "ja" | "ko" | "vi" | "id";

export interface LongstayLocaleCopy {
  hreflang: string;
  title: string;
  description: string;
  h1: string;
  lead: string;
  benefits: Array<[string, string]>;
  sections: Array<{ heading: string; paragraphs: string[] }>;
  faqs: Array<{ q: string; a: string }>;
  ctaTitle: string;
  ctaBody: string;
  breadcrumbHome: string;
  breadcrumbPillar: string;
  seeServices: string;
  seeCountries: string;
}


export const LONGSTAY_LOCALE_COPY: Record<LongstayLocale, LongstayLocaleCopy> = {
  en: {
    hreflang: "en",
    title: "Thailand Long-stay Visas — Retirement, LTR, Elite, PR",
    description:
      "Licensed Thai immigration lawyers: Non-O/O-A/O-X retirement, LTR 10-year, Thailand Privilege (Elite) 5/10 years, Permanent Residence. Fixed quotes.",
    h1: "Long-stay in Thailand — Retirement, LTR, Thailand Privilege & PR",
    lead:
      "Thai Notary Law helps foreigners aged 50+, remote professionals, high-net-worth families and long-term investors design the right long-stay pathway to Thailand. We compare Non-O retirement, LTR (Wealthy Global Citizen, Pensioner, Work-From-Thailand, Highly-Skilled), Thailand Privilege (Gold 5 yrs / Platinum 10 yrs) and Permanent Residence, then handle every step end-to-end.",
    benefits: [
      ["Bar-registered immigration lawyers", "Real Thai lawyers registered with the Lawyers Council of Thailand — not agents or brokers."],
      ["Comparison brief", "Every engagement starts with a written 2-3 pathway comparison covering cost, timeline, tax, mobility and family coverage."],
      ["BOI / TPC / Immigration relationships", "Direct filing with BOI (LTR), Thailand Privilege Card (Elite) and Immigration Bureau — no third-party markup."],
      ["Insurance & finance advisory", "We arrange compliant health insurance (OPD 40k + IPD 400k THB) and coach 800,000 THB / 65k THB pension deposits."],
      ["Roadmap to PR & citizenship", "We plan 5-10 years ahead so today's visa becomes tomorrow's PR and next decade's Thai citizenship."],
      ["Post-visa compliance", "90-day (TM.47), TM.30, re-entry permits, extension calendar — all managed in-house."],
    ],
    sections: [
      {
        heading: "Choosing between LTR, Elite, Non-O retirement and PR",
        paragraphs: [
          "LTR is Thailand's flagship 10-year visa with a 17% flat tax rate for Highly-Skilled Professionals, work permit rights and dependents included. It suits remote employees earning USD 80k+, retirees with USD 80k+ pension, or high-net-worth families with USD 1M assets plus USD 500k Thai investment.",
          "Thailand Privilege (formerly Elite) is a paid membership — Gold at 650,000 THB for 5 years or Platinum at 1,500,000 THB for 10 years. No income proof required, VIP airport service and unlimited entries. Best for retirees who do not meet Non-O deposit thresholds and remote workers who don't qualify for LTR.",
          "Non-O retirement at 50+ needs 800,000 THB in a Thai bank for 2 months before filing, or 65,000 THB/month pension letter, or a combination. Annual renewal at 1,900 THB fee. Cheapest option but most administrative.",
          "Permanent Residence takes 3 continuous years on Non-B/Non-O with work permit, tax records and language interview. Once granted you keep your status without renewals and can eventually naturalise.",
        ],
      },
      {
        heading: "Documents, finance and insurance checklist",
        paragraphs: [
          `Financial: 12-month bank statement, sponsor letter for dependents, tax return home country, source-of-funds letter for TPC/LTR (state agencies).`,
          "Health: insurance with OPD 40k + IPD 400k THB (Non-O-A/O-X) or USD 50k/year (LTR) from a CoIT-listed insurer, plus a medical certificate under 90 days old.",
          "Personal history: police check, marriage/birth certificates, all with Apostille or chain legalisation and MoJ-certified Thai translation.",
        ],
      },
      {
        heading: "Post-visa compliance we run for you",
        paragraphs: [
          "90-day reporting (TM.47) via online portal, always filed 15 days early to leave buffer for portal outages.",
          "TM.30 within 24h of every address change — we file it for tenants who prefer not to deal with landlords.",
          "Re-entry permits, annual extension appointments, insurance renewals — one calendar, one contact, one bill.",
        ],
      },
    ],
    faqs: [
      { q: "LTR vs Thailand Privilege — which is better?", a: "LTR is better if you qualify (real 10-year visa, work permit, 17% tax). Privilege is faster and needs no income proof but costs 650k-1.5M THB upfront and does not grant a work permit." },
      { q: "Does 800,000 THB really need to be untouched for 3 months?", a: "Yes. Immigration requires the 800k to sit in a Thai bank account in your name for 2 months before filing and 3 months after issuance, then a minimum of 400k for the remaining 7 months." },
      { q: "Can my spouse and children join?", a: "LTR, Elite and PR cover a spouse and children under 20. Non-O retirement covers a spouse only — children apply for a separate Non-O dependent visa." },
      { q: "Do I owe Thai tax if I stay long-term?", a: "Being 180+ days in Thailand makes you a tax resident. From 2024 income remitted from abroad is taxable. LTR Highly-Skilled Professionals pay a flat 17%. Retirement and Elite holders pay standard progressive rates on remitted foreign income." },
      { q: "Are your quoted prices final?", a: "Our fee is fixed in the engagement letter. Government fees (TPC 650k THB, BOI 50k THB, PR 191,400 THB) are quoted separately and paid direct to the agency." },
    ],
    ctaTitle: "Book your 30-minute long-stay consultation — free",
    ctaBody: "Phone · LINE · Email · Zoom. We produce a written 2-3 pathway comparison before you decide.",
    breadcrumbHome: "Home",
    breadcrumbPillar: "Long-stay",
    seeServices: "All long-stay services",
    seeCountries: "By nationality",
  },
  zh: {
    hreflang: "zh",
    title: "泰国长期居留签证 — 退休/LTR/精英卡/永居 | Thai Notary Law",
    description:
      "泰国持牌移民律师团队,提供退休Non-O/O-A/O-X、LTR十年签证、Thailand Privilege(精英卡)5/10年及永久居留一站式服务。固定报价,透明时间线。",
    h1: "泰国长期居留 — 退休 · LTR · Thailand Privilege · 永居",
    lead:
      "Thai Notary Law 帮助 50 岁以上外国人、远程工作者、高净值家庭和长期投资者规划最适合的泰国长期居留路径。我们对比 Non-O 退休、LTR(全球富裕人士 · 退休金 · 泰国远程工作 · 高技能人才)、Thailand Privilege(Gold 5年 / Platinum 10年)以及永久居留,并端到端处理每一步。",
    benefits: [
      ["泰国律师公会持牌律师", "由泰国律师公会注册的执业律师,而非中介或代办。"],
      ["路径比较书", "每一次服务都以书面 2-3 条路径比较开始:成本 · 时间 · 税 · 出入境 · 家人覆盖。"],
      ["BOI / TPC / 移民局直办", "LTR 直连 BOI、精英卡直连 TPC,不加第三方溢价。"],
      ["保险与资金辅导", "协助购买符合公告的健康保险(OPD 4万+IPD 40万泰铢),辅导 80 万泰铢或月养老金 6.5 万泰铢证明。"],
      ["永居与国籍路线图", "提前规划 5-10 年,让今天的签证成为明天的永居、后天的泰国国籍。"],
      ["签证后合规", "90 天报到(TM.47)、TM.30、回头签、续签日历 — 内部一站管理。"],
    ],
    sections: [
      {
        heading: "LTR、精英卡、Non-O 退休、永居如何选?",
        paragraphs: [
          "LTR 是泰国旗舰十年签证,高技能人才享 17% 平税,含工作许可,涵盖家属。适合月薪 8 万美元以上的远程员工、年养老金 8 万美元以上的退休者,或资产 100 万美元+ 泰国投资 50 万美元的高净值家庭。",
          "Thailand Privilege(原精英卡)是付费会员:Gold 65 万泰铢 5 年 / Platinum 150 万泰铢 10 年。无需收入证明,含 VIP 机场接送,不限次入境。适合不符合 Non-O 存款条件的退休者与不合 LTR 的远程工作者。",
          "Non-O 退休需年满 50 岁 + 泰国银行存款 80 万泰铢连续 2 个月,或月养老金 6.5 万泰铢证明,年年续签费 1,900 泰铢。最便宜但手续最多。",
          "永久居留(PR)需 Non-B/Non-O 连续 3 年 + 工作许可 + 纳税记录 + 泰语面试。获批后免续签,可最终归化为泰国国籍。",
        ],
      },
      {
        heading: "文件、资金与保险清单",
        paragraphs: [
          "财务:12 个月银行流水、家属担保信、原籍国报税单、TPC/LTR 资金来源说明。",
          "健康:OPD 4万+IPD 40万泰铢保险(Non-O-A/O-X)或 5 万美元/年(LTR),由 CoIT 名单内保险公司承保,附 90 天内体检报告。",
          "个人:无犯罪记录、结婚/出生证明,均需加签或链式合法化 + 司法部认证泰文翻译。",
        ],
      },
      {
        heading: "签证后合规我们代办",
        paragraphs: [
          "90 天报到(TM.47)在线申报,提前 15 天以防系统故障。",
          "TM.30 每次更换住址 24 小时内申报 — 我们替不愿麻烦房东的租户申报。",
          "回头签、年度续签预约、保险续保 — 一份日历、一个联系人、一份账单。",
        ],
      },
    ],
    faqs: [
      { q: "LTR 与精英卡怎么选?", a: "符合资格首选 LTR(真正十年签、含工作许可、17% 税率)。精英卡更快且无收入门槛,但预付 65 万-150 万泰铢且不含工作许可。" },
      { q: "80 万泰铢真的必须冻结 3 个月吗?", a: "是的。申请前 2 个月必须已在您本人的泰国银行账户,批准后再保留 3 个月,其余 7 个月至少 40 万泰铢。" },
      { q: "配偶和子女可以一起吗?", a: "LTR、精英卡、永居涵盖配偶及未满 20 岁子女。Non-O 退休仅涵盖配偶,子女须另办 Non-O 家属签。" },
      { q: "长期居留是否需在泰国交税?", a: "每年在泰 180 天以上即为税务居民。2024 年起海外汇入所得应税。LTR 高技能人才享 17% 平税,其他类别按累进税率。" },
      { q: "报价是最终价吗?", a: "律师费在委托书中固定。政府规费(精英卡 65 万、BOI 5 万、永居 191,400 泰铢)另列,直付主管机关。" },
    ],
    ctaTitle: "预约 30 分钟免费长期居留咨询",
    ctaBody: "电话 · LINE · 邮件 · Zoom。我们在您决定前提供书面 2-3 条路径比较。",
    breadcrumbHome: "首页",
    breadcrumbPillar: "长期居留",
    seeServices: "所有长期居留服务",
    seeCountries: "按国籍浏览",
  },
  ja: {
    hreflang: "ja",
    title: "タイ長期滞在ビザ — リタイアメント/LTR/エリート/永住 | Thai Notary Law",
    description:
      "タイの登録移民弁護士による長期滞在サポート:リタイアメントNon-O/O-A/O-X、LTR 10年ビザ、Thailand Privilege(エリート)5/10年、永住権。固定料金・明確なスケジュール。",
    h1: "タイ長期滞在 — リタイアメント・LTR・Thailand Privilege・永住権",
    lead:
      "Thai Notary Law は 50 歳以上の外国人、リモートワーカー、富裕層ファミリー、長期投資家に最適なタイ長期滞在ルートをデザインします。Non-O リタイアメント、LTR(Wealthy Global Citizen · Pensioner · WFTP · HSP)、Thailand Privilege(Gold 5年 / Platinum 10年)、永住権を比較検討し、申請から取得後のコンプライアンスまで一貫対応します。",
    benefits: [
      ["タイ弁護士会登録弁護士", "エージェントではなくタイ弁護士会に登録された弁護士が担当。"],
      ["比較ブリーフ", "各案件は 2-3 ルートの比較書(費用・期間・税・移動性・家族範囲)から開始。"],
      ["BOI / TPC / 入管直接申請", "LTR は BOI、エリートは Thailand Privilege Card、通常ビザは入管に直接申請。第三者マージンなし。"],
      ["保険・財務アドバイザリー", "OPD 4万+IPD 40万バーツの適合保険、80万バーツ/月6.5万バーツ年金の預金設計。"],
      ["永住・帰化ロードマップ", "5-10 年先を見据え、今日のビザを明日の永住、10 年後の国籍取得へ。"],
      ["取得後のコンプライアンス", "90日レポート(TM.47)、TM.30、リエントリー、更新カレンダーを一括管理。"],
    ],
    sections: [
      {
        heading: "LTR・エリート・Non-O リタイアメント・永住権の使い分け",
        paragraphs: [
          "LTR はタイの旗艦 10 年ビザで、高技能人材(HSP)は 17% の均一税率、就労許可付き、家族含む。月 8,000 米ドル以上のリモート従業員、8 万米ドル以上の年金受給者、資産 100 万米ドル+タイ投資 50 万米ドルの富裕層に適します。",
          "Thailand Privilege(旧エリート)は有料会員制:Gold 65 万バーツ 5 年 / Platinum 150 万バーツ 10 年。所得証明不要、空港 VIP サービスと無制限入国。Non-O 預金要件を満たさないリタイアメント希望者や LTR 非該当者向け。",
          "Non-O リタイアメントは 50 歳以上、タイ銀行に 80 万バーツを申請 2 か月前から預入、または月 6.5 万バーツの年金証明が必要。更新料は年 1,900 バーツで最安ですが手続きは最多。",
          "永住権は Non-B/Non-O を 3 年連続保持 + 就労許可 + 納税実績 + タイ語面接。取得後は更新不要で最終的にタイ国籍取得も可能。",
        ],
      },
      {
        heading: "書類・財務・保険チェックリスト",
        paragraphs: [
          "財務:12 か月銀行明細、家族用スポンサーレター、母国確定申告、TPC/LTR 用資金源証明書。",
          "健康:CoIT 掲載保険会社の OPD 4万+IPD 40万バーツ(Non-O-A/O-X)または 5 万米ドル/年(LTR)、90 日以内の健康診断書。",
          "経歴:警察証明、婚姻/出生証明にアポスティーユまたはチェーン認証 + 司法省認証タイ語翻訳。",
        ],
      },
      {
        heading: "取得後コンプライアンス代行",
        paragraphs: [
          "90 日レポート(TM.47)はオンライン申請、システム障害に備え 15 日前倒し。",
          "TM.30 は住所変更 24 時間以内 — 大家との交渉が煩わしい入居者に代行。",
          "リエントリーパーミット、年次更新予約、保険更新を一つのカレンダーで管理。",
        ],
      },
    ],
    faqs: [
      { q: "LTR とエリートどちらが良い?", a: "資格があれば LTR が優位(真の 10 年、就労許可、17% 税率)。エリートは所得要件なしで即決可能ですが 65 万-150 万バーツ前払い、就労許可なし。" },
      { q: "80 万バーツは本当に 3 か月動かせない?", a: "はい。申請 2 か月前からご本人名義タイ口座で保持、承認後 3 か月維持、その後 7 か月は最低 40 万バーツ。" },
      { q: "配偶者と子供も同行可能?", a: "LTR・エリート・永住は配偶者と 20 歳未満の子供をカバー。Non-O リタイアメントは配偶者のみ、子供は別途 Non-O 扶養ビザ。" },
      { q: "長期滞在でタイの税金は?", a: "年間 180 日以上でタイ居住者となり、2024 年から海外送金所得も課税対象。LTR HSP は 17% 均一、その他は累進課税。" },
      { q: "提示価格は最終価格?", a: "弁護士報酬は委任契約書で固定。政府手数料(エリート 65 万、BOI 5 万、永住 191,400 バーツ)は別途で当局に直接支払い。" },
    ],
    ctaTitle: "30 分間の無料長期滞在コンサルテーションを予約",
    ctaBody: "電話・LINE・メール・Zoom 対応。決定前に書面での 2-3 ルート比較を提供します。",
    breadcrumbHome: "ホーム",
    breadcrumbPillar: "長期滞在",
    seeServices: "長期滞在サービス一覧",
    seeCountries: "国籍別",
  },
  ko: {
    hreflang: "ko",
    title: "태국 장기 거주 비자 — 은퇴/LTR/엘리트/영주권 | Thai Notary Law",
    description:
      "태국 등록 이민 변호사 팀 — Non-O/O-A/O-X 은퇴 비자, LTR 10년 비자, Thailand Privilege(엘리트) 5/10년, 영주권까지 원스톱. 고정 견적, 투명한 일정.",
    h1: "태국 장기 거주 — 은퇴 · LTR · Thailand Privilege · 영주권",
    lead:
      "Thai Notary Law는 50세 이상 외국인, 원격 근무자, 고순자산 가족, 장기 투자자에게 가장 적합한 태국 장기 거주 경로를 설계합니다. Non-O 은퇴, LTR(WGC · Pensioner · WFTP · HSP), Thailand Privilege(Gold 5년 / Platinum 10년), 영주권을 비교하고 신청부터 사후 컴플라이언스까지 일괄 처리합니다.",
    benefits: [
      ["태국 변호사협회 등록 변호사", "에이전트가 아닌 태국 변호사협회 정식 등록 변호사가 직접 담당."],
      ["경로 비교서", "모든 사건은 2-3개 경로 비교서(비용·기간·세금·이동성·가족 포함)로 시작."],
      ["BOI / TPC / 이민국 직접 신청", "LTR은 BOI, 엘리트는 Thailand Privilege Card, 이민 비자는 이민국으로 직접 신청 — 중간 마진 없음."],
      ["보험 및 재정 자문", "OPD 4만+IPD 40만 바트 보험 배정, 80만 바트 / 월 6.5만 바트 연금 서류 코칭."],
      ["영주권/시민권 로드맵", "5-10년 앞을 내다보고 오늘의 비자를 내일의 영주권, 10년 후 태국 국적으로 연결."],
      ["비자 후 컴플라이언스", "90일 신고(TM.47), TM.30, 재입국 허가, 갱신 캘린더를 사내 관리."],
    ],
    sections: [
      {
        heading: "LTR · 엘리트 · Non-O 은퇴 · 영주권 선택 기준",
        paragraphs: [
          "LTR은 태국의 대표 10년 비자로 고숙련 전문가(HSP)에게 17% 균일세율, 취업 허가 포함, 부양가족 포함. 월 8천 달러 이상 원격 근무자, 연 8만 달러 이상 연금 수령자, 자산 100만 달러 + 태국 투자 50만 달러 이상 고자산 가족에게 적합.",
          "Thailand Privilege(구 엘리트)는 유료 회원제로 Gold 65만 바트 5년, Platinum 150만 바트 10년. 소득 증명 불필요, 공항 VIP 서비스, 무제한 입국. Non-O 예치 요건을 못 맞추는 은퇴자, LTR 요건에 못 맞는 원격 근무자에게 적합.",
          "Non-O 은퇴는 50세 이상, 태국 은행에 80만 바트를 신청 2개월 전부터 예치, 또는 월 6.5만 바트 연금 증명 필요. 연간 갱신비 1,900 바트 — 가장 저렴하지만 절차 최다.",
          "영주권은 Non-B/Non-O 3년 연속 유지 + 취업 허가 + 세금 기록 + 태국어 면접. 취득 후 갱신 불요, 궁극적으로 국적 취득 가능.",
        ],
      },
      {
        heading: "서류·재정·보험 체크리스트",
        paragraphs: [
          "재정: 12개월 은행 거래내역, 부양가족 스폰서 레터, 본국 세금신고서, TPC/LTR용 자금 출처 설명서.",
          "건강: CoIT 등재 보험사의 OPD 4만+IPD 40만 바트(Non-O-A/O-X) 또는 연 5만 달러(LTR), 90일 이내 건강진단서.",
          "경력: 무범죄 증명서, 혼인/출생증명서에 아포스티유 또는 체인 인증 + 법무부 인증 태국어 번역.",
        ],
      },
      {
        heading: "비자 취득 후 컴플라이언스 대행",
        paragraphs: [
          "90일 신고(TM.47) 온라인 신고, 시스템 장애 대비 15일 조기 제출.",
          "TM.30은 주소 변경 24시간 이내 — 집주인과의 교섭을 피하고 싶은 임차인 대신 제출.",
          "재입국 허가, 연간 갱신 예약, 보험 갱신을 하나의 캘린더로 관리.",
        ],
      },
    ],
    faqs: [
      { q: "LTR과 엘리트 어느 쪽이 유리?", a: "자격이 있다면 LTR 우선(진짜 10년, 취업 허가, 17% 세율). 엘리트는 소득 증명 없이 즉시 결정 가능하나 65만-150만 바트 선불, 취업 허가 없음." },
      { q: "80만 바트를 정말 3개월간 손대면 안 되나요?", a: "네. 신청 2개월 전부터 본인 명의 태국 계좌에 예치, 승인 후 3개월 유지, 이후 7개월은 최소 40만 바트 유지." },
      { q: "배우자와 자녀도 동반 가능?", a: "LTR·엘리트·영주권은 배우자 및 20세 미만 자녀 포함. Non-O 은퇴는 배우자만, 자녀는 별도 Non-O 부양가족 비자." },
      { q: "장기 체류 시 태국에서 세금?", a: "연 180일 이상 체류 시 태국 세무거주자로 2024년부터 해외 송금 소득에도 과세. LTR HSP는 17% 균일, 그 외는 누진세율." },
      { q: "제시 가격이 최종 가격인가요?", a: "변호사 수임료는 위임장에서 고정. 정부 수수료(엘리트 65만, BOI 5만, 영주권 191,400 바트)는 별도로 관계 기관에 직접 납부." },
    ],
    ctaTitle: "30분 무료 장기 거주 상담 예약",
    ctaBody: "전화 · LINE · 이메일 · Zoom. 결정 전에 서면 2-3 경로 비교 제공.",
    breadcrumbHome: "홈",
    breadcrumbPillar: "장기 거주",
    seeServices: "장기 거주 서비스 전체",
    seeCountries: "국적별",
  },
  vi: {
    hreflang: "vi",
    title: "Visa Cư trú Dài hạn Thái Lan — Hưu trí, LTR, Elite, PR",
    description:
      "Luật sư di trú cấp phép — hưu trí Non-O/O-A/O-X, LTR 10 năm, Thailand Privilege 5/10 năm, Thường trú. Báo giá cố định, lộ trình minh bạch.",
    h1: "Cư trú Dài hạn tại Thái Lan — Hưu trí · LTR · Thailand Privilege · Thường trú",
    lead:
      "Thai Notary Law giúp người nước ngoài trên 50 tuổi, chuyên gia làm việc từ xa, gia đình có tài sản cao và nhà đầu tư dài hạn thiết kế lộ trình cư trú dài hạn phù hợp nhất tại Thái Lan. Chúng tôi so sánh Non-O hưu trí, LTR (WGC · Pensioner · WFTP · HSP), Thailand Privilege (Gold 5 năm / Platinum 10 năm) và Thường trú, rồi xử lý mọi bước từ nộp hồ sơ đến tuân thủ sau khi được cấp.",
    benefits: [
      ["Luật sư đăng ký Hiệp hội Luật sư Thái Lan", "Luật sư chính danh, không phải môi giới."],
      ["Bản so sánh lộ trình", "Mỗi vụ việc bắt đầu bằng bản so sánh 2-3 lộ trình bằng văn bản (chi phí · thời gian · thuế · di chuyển · phạm vi gia đình)."],
      ["Nộp trực tiếp BOI / TPC / Cục Di trú", "LTR nộp thẳng BOI, Elite qua Thailand Privilege Card, visa thường qua Cục Di trú — không tính phí trung gian."],
      ["Tư vấn bảo hiểm & tài chính", "Sắp xếp bảo hiểm y tế đạt chuẩn (OPD 40k + IPD 400k THB), hướng dẫn tiền gửi 800.000 THB hoặc lương hưu 65.000 THB/tháng."],
      ["Lộ trình Thường trú & Quốc tịch", "Lập kế hoạch 5-10 năm để visa hôm nay thành thường trú ngày mai và quốc tịch Thái Lan sau này."],
      ["Tuân thủ sau visa", "Báo cáo 90 ngày (TM.47), TM.30, giấy phép tái nhập, lịch gia hạn — quản lý nội bộ."],
    ],
    sections: [
      {
        heading: "So sánh LTR, Elite, Non-O hưu trí và Thường trú",
        paragraphs: [
          "LTR là visa 10 năm hàng đầu của Thái Lan, HSP hưởng thuế suất bằng 17%, kèm giấy phép lao động, bao gồm người phụ thuộc. Phù hợp cho nhân viên làm từ xa thu nhập từ 80.000 USD/năm, người nghỉ hưu có lương hưu 80.000 USD, hoặc gia đình có tài sản 1 triệu USD + đầu tư 500.000 USD tại Thái Lan.",
          "Thailand Privilege (Elite cũ) là hội viên trả phí: Gold 650.000 THB cho 5 năm / Platinum 1.500.000 THB cho 10 năm. Không cần chứng minh thu nhập, dịch vụ VIP sân bay, nhập cảnh không giới hạn. Phù hợp cho người nghỉ hưu không đủ điều kiện tiền gửi Non-O và người làm từ xa không đủ điều kiện LTR.",
          "Non-O hưu trí yêu cầu 50 tuổi+ và 800.000 THB gửi ngân hàng Thái Lan liên tục 2 tháng trước nộp, hoặc lương hưu 65.000 THB/tháng, hoặc kết hợp. Phí gia hạn hàng năm 1.900 THB — rẻ nhất nhưng nhiều thủ tục nhất.",
          "Thường trú yêu cầu Non-B/Non-O liên tục 3 năm + giấy phép lao động + hồ sơ nộp thuế + phỏng vấn tiếng Thái. Sau khi được cấp không cần gia hạn và có thể nhập tịch cuối cùng.",
        ],
      },
      {
        heading: "Danh mục hồ sơ, tài chính và bảo hiểm",
        paragraphs: [
          "Tài chính: sao kê ngân hàng 12 tháng, thư bảo lãnh cho người phụ thuộc, tờ khai thuế nước sở tại, giải trình nguồn tiền cho TPC/LTR.",
          "Y tế: bảo hiểm OPD 40k + IPD 400k THB (Non-O-A/O-X) hoặc 50k USD/năm (LTR) từ công ty trong danh sách CoIT, giấy khám sức khỏe không quá 90 ngày.",
          "Cá nhân: lý lịch tư pháp, giấy đăng ký kết hôn/khai sinh có Apostille hoặc hợp pháp hóa dây chuyền + bản dịch tiếng Thái công chứng của Bộ Tư pháp.",
        ],
      },
      {
        heading: "Tuân thủ sau visa do chúng tôi thực hiện",
        paragraphs: [
          "Báo cáo 90 ngày (TM.47) qua cổng trực tuyến, luôn nộp sớm 15 ngày để phòng sự cố hệ thống.",
          "TM.30 trong vòng 24 giờ khi đổi chỗ ở — chúng tôi nộp thay cho người thuê không muốn làm việc với chủ nhà.",
          "Giấy phép tái nhập, lịch hẹn gia hạn hàng năm, gia hạn bảo hiểm — cùng một lịch, một đầu mối, một hóa đơn.",
        ],
      },
    ],
    faqs: [
      { q: "LTR hay Elite tốt hơn?", a: "Nếu đủ điều kiện chọn LTR (10 năm thực, có giấy phép lao động, thuế 17%). Elite nhanh hơn và không cần chứng minh thu nhập nhưng phải trả trước 650k-1,5M THB và không có giấy phép lao động." },
      { q: "800.000 THB thực sự phải giữ nguyên 3 tháng?", a: "Đúng. Cần nằm trong tài khoản ngân hàng Thái Lan mang tên bạn 2 tháng trước khi nộp và 3 tháng sau khi cấp, sau đó 7 tháng còn lại giữ tối thiểu 400k." },
      { q: "Vợ/chồng và con có thể đi cùng?", a: "LTR, Elite và Thường trú bao gồm vợ/chồng và con dưới 20 tuổi. Non-O hưu trí chỉ bao gồm vợ/chồng, con phải xin visa Non-O phụ thuộc riêng." },
      { q: "Ở lâu dài có phải nộp thuế Thái Lan?", a: "Từ 180 ngày/năm trở lên là cư trú thuế. Từ 2024, thu nhập chuyển từ nước ngoài về Thái Lan bị đánh thuế. LTR HSP hưởng 17%, các loại khác theo biểu lũy tiến." },
      { q: "Giá đã báo là giá cuối cùng?", a: "Phí luật sư cố định trong hợp đồng. Phí nhà nước (Elite 650k, BOI 50k, Thường trú 191,400 THB) được báo giá riêng và nộp trực tiếp cơ quan." },
    ],
    ctaTitle: "Đặt lịch tư vấn dài hạn 30 phút miễn phí",
    ctaBody: "Điện thoại · LINE · Email · Zoom. Chúng tôi cung cấp bản so sánh 2-3 lộ trình bằng văn bản trước khi bạn quyết định.",
    breadcrumbHome: "Trang chủ",
    breadcrumbPillar: "Cư trú dài hạn",
    seeServices: "Tất cả dịch vụ dài hạn",
    seeCountries: "Theo quốc tịch",
  },
  id: {
    hreflang: "id",
    title: "Visa Tinggal Lama Thailand — Pensiun, LTR, Elite, PR",
    description:
      "Pengacara imigrasi berlisensi: Non-O/O-A/O-X pensiun, LTR 10 tahun, Thailand Privilege 5/10 tahun, Izin Tinggal Tetap. Kutipan tetap.",
    h1: "Tinggal Jangka Panjang di Thailand — Pensiun · LTR · Thailand Privilege · PR",
    lead:
      "Thai Notary Law membantu warga asing 50+, pekerja jarak jauh, keluarga bernilai tinggi, dan investor jangka panjang merancang jalur tinggal jangka panjang terbaik di Thailand. Kami membandingkan Non-O pensiun, LTR (WGC · Pensioner · WFTP · HSP), Thailand Privilege (Gold 5 tahun / Platinum 10 tahun), dan Izin Tinggal Tetap, lalu menangani semua langkah dari pengajuan hingga kepatuhan pasca-visa.",
    benefits: [
      ["Pengacara terdaftar Lawyers Council of Thailand", "Pengacara sesungguhnya, bukan agen atau broker."],
      ["Perbandingan jalur", "Setiap kasus dimulai dengan brief perbandingan 2-3 jalur (biaya · waktu · pajak · mobilitas · cakupan keluarga)."],
      ["Pengajuan langsung BOI / TPC / Imigrasi", "LTR langsung ke BOI, Elite via Thailand Privilege Card, visa imigrasi langsung ke Kantor Imigrasi — tanpa markup pihak ketiga."],
      ["Konsultasi asuransi & keuangan", "Menyusun asuransi kesehatan sesuai regulasi (OPD 40k + IPD 400k THB) dan pelatihan setoran 800.000 THB atau pensiun 65.000 THB/bulan."],
      ["Peta jalan menuju PR & kewarganegaraan", "Merencanakan 5-10 tahun ke depan agar visa hari ini menjadi PR besok dan kewarganegaraan Thailand kelak."],
      ["Kepatuhan pasca-visa", "Laporan 90 hari (TM.47), TM.30, izin masuk kembali, kalender perpanjangan — dikelola internal."],
    ],
    sections: [
      {
        heading: "Memilih antara LTR, Elite, Non-O pensiun, dan PR",
        paragraphs: [
          "LTR adalah visa unggulan 10 tahun Thailand dengan tarif pajak tetap 17% untuk Highly-Skilled Professional, izin kerja termasuk, dan mencakup anggota keluarga. Cocok untuk karyawan remote berpenghasilan USD 80k+, pensiunan dengan pensiun USD 80k+, atau keluarga bernilai USD 1M dengan investasi USD 500k di Thailand.",
          "Thailand Privilege (dulu Elite) adalah keanggotaan berbayar: Gold 650.000 THB untuk 5 tahun / Platinum 1.500.000 THB untuk 10 tahun. Tidak memerlukan bukti penghasilan, layanan VIP bandara, dan masuk tanpa batas. Cocok untuk pensiunan yang tidak memenuhi setoran Non-O dan pekerja remote yang tidak memenuhi LTR.",
          "Non-O pensiun memerlukan usia 50+ dengan 800.000 THB di bank Thailand 2 bulan sebelum pengajuan, atau surat pensiun 65.000 THB/bulan, atau kombinasi. Biaya perpanjangan tahunan 1.900 THB — termurah tetapi paling administratif.",
          "Izin Tinggal Tetap memerlukan Non-B/Non-O berturut-turut 3 tahun + izin kerja + catatan pajak + wawancara bahasa Thai. Setelah diberikan tidak perlu perpanjangan dan pada akhirnya dapat menjadi warga negara.",
        ],
      },
      {
        heading: "Daftar dokumen, keuangan, dan asuransi",
        paragraphs: [
          "Keuangan: mutasi bank 12 bulan, surat sponsor untuk tanggungan, SPT negara asal, surat sumber dana untuk TPC/LTR.",
          "Kesehatan: asuransi OPD 40k + IPD 400k THB (Non-O-A/O-X) atau USD 50k/tahun (LTR) dari perusahaan dalam daftar CoIT, surat kesehatan kurang dari 90 hari.",
          "Pribadi: SKCK, akta pernikahan/kelahiran dengan Apostille atau legalisasi berantai + terjemahan bahasa Thai bersertifikat Kemenkumham.",
        ],
      },
      {
        heading: "Kepatuhan pasca-visa yang kami jalankan",
        paragraphs: [
          "Laporan 90 hari (TM.47) online, selalu diajukan 15 hari lebih awal sebagai buffer gangguan portal.",
          "TM.30 dalam 24 jam setiap perubahan alamat — kami ajukan untuk penyewa yang tak ingin repot dengan pemilik.",
          "Izin masuk kembali, janji perpanjangan tahunan, perpanjangan asuransi — satu kalender, satu kontak, satu tagihan.",
        ],
      },
    ],
    faqs: [
      { q: "LTR vs Thailand Privilege — mana lebih baik?", a: "LTR lebih baik jika memenuhi syarat (visa 10 tahun sesungguhnya, izin kerja, pajak 17%). Privilege lebih cepat dan tanpa bukti penghasilan tapi biaya 650k-1,5M THB di muka dan tidak memberi izin kerja." },
      { q: "Apakah 800.000 THB benar-benar harus utuh 3 bulan?", a: "Ya. Wajib duduk di rekening bank Thailand atas nama Anda 2 bulan sebelum pengajuan dan 3 bulan setelah diterbitkan, lalu minimum 400k selama 7 bulan sisa." },
      { q: "Bolehkah pasangan dan anak ikut?", a: "LTR, Elite, dan PR mencakup pasangan dan anak di bawah 20 tahun. Non-O pensiun hanya mencakup pasangan — anak mengajukan Non-O tanggungan terpisah." },
      { q: "Apakah saya kena pajak Thailand jika tinggal lama?", a: "Tinggal 180+ hari/tahun menjadikan Anda penduduk pajak. Sejak 2024, penghasilan yang diremit dari luar negeri kena pajak. LTR HSP membayar 17% flat; kategori lain mengikuti tarif progresif." },
      { q: "Apakah harga yang dikutip final?", a: "Fee kami tetap dalam surat penunjukan. Biaya pemerintah (TPC 650k, BOI 50k, PR 191,400 THB) dikutip terpisah dan dibayar langsung ke instansi." },
    ],
    ctaTitle: "Jadwalkan konsultasi 30 menit gratis untuk tinggal jangka panjang",
    ctaBody: "Telepon · LINE · Email · Zoom. Kami memberikan perbandingan 2-3 jalur tertulis sebelum Anda memutuskan.",
    breadcrumbHome: "Beranda",
    breadcrumbPillar: "Tinggal Jangka Panjang",
    seeServices: "Semua layanan tinggal jangka panjang",
    seeCountries: "Berdasarkan kewarganegaraan",
  },
};

export const LONGSTAY_HREFLANG_LINKS = [
  { rel: "alternate" as const, hreflang: "th", href: "https://thainotarylaw.com/longstay" },
  { rel: "alternate" as const, hreflang: "en", href: "https://thainotarylaw.com/en/longstay" },
  { rel: "alternate" as const, hreflang: "zh", href: "https://thainotarylaw.com/zh/longstay" },
  { rel: "alternate" as const, hreflang: "ja", href: "https://thainotarylaw.com/ja/longstay" },
  { rel: "alternate" as const, hreflang: "ko", href: "https://thainotarylaw.com/ko/longstay" },
  { rel: "alternate" as const, hreflang: "vi", href: "https://thainotarylaw.com/vi/longstay" },
  { rel: "alternate" as const, hreflang: "id", href: "https://thainotarylaw.com/id/longstay" },
  { rel: "alternate" as const, hreflang: "x-default", href: "https://thainotarylaw.com/longstay" },
];
